import React from "react";
import { FiShield, FiRefreshCw, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

function MathCaptcha({
  captcha,
  question: propQuestion,
  value: propValue,
  onChange: propOnChange,
  onRefresh: propOnRefresh,
  error: propError,
  isValid: propIsValid,
  isTouched: propIsTouched,
  isRefreshing: propIsRefreshing,
  inputRef: propInputRef,
  className = "",
}) {
  const question = captcha ? captcha.question : propQuestion;
  const value = captcha ? captcha.value : propValue;
  const onChange = captcha ? captcha.handleChange : propOnChange;
  const onRefresh = captcha ? captcha.refreshCaptcha : propOnRefresh;
  const error = captcha ? captcha.error : propError;
  const isValid = captcha ? captcha.isValid : propIsValid;
  const isTouched = captcha ? captcha.isTouched : propIsTouched;
  const isRefreshing = captcha ? captcha.isRefreshing : propIsRefreshing;
  const inputRef = captcha ? captcha.inputRef : propInputRef;

  const handleInputChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={`space-y-3.5 ${className}`.trim()}>
      {/* Section Label */}
      <div className="flex items-center gap-1.5">
        <FiShield className="h-4 w-4 text-[#b97a32]" aria-hidden="true" />
        <label
          htmlFor="captcha-answer"
          className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 cursor-pointer"
        >
          Security Verification
        </label>
        <span className="text-xs font-bold text-danger" title="Required">*</span>
      </div>

      {/* Captcha Card Container */}
      <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-3.5 transition-all dark:border-[#374151] dark:bg-[#1f2937]/70 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-3">
          {/* Question Box & Refresh Button */}
          <div className="flex h-11 flex-1 items-center justify-between rounded-lg border border-[#E2E8F0] bg-white px-3.5 shadow-sm transition-all dark:border-[#4B5563] dark:bg-[#111c33]/80">
            <span className="select-none font-mono text-base font-bold tracking-wide text-[#111c33] dark:text-[#f3f4f6] sm:text-lg">
              {question}
            </span>
            <button
              type="button"
              onClick={onRefresh}
              title="Refresh security question"
              aria-label="Refresh security question"
              className={`flex h-10 w-10 min-w-[40px] min-h-[40px] items-center justify-center rounded-lg text-slate-500 transition-all hover:bg-[#b97a32]/10 hover:text-[#b97a32] focus:outline-none focus:ring-2 focus:ring-[#b97a32]/40 active:scale-95 dark:text-slate-400 dark:hover:bg-[#b97a32]/20 dark:hover:text-[#b97a32] ${
                isRefreshing ? "animate-spin text-[#b97a32]" : ""
              }`}
            >
              <FiRefreshCw className="h-4 w-4 transition-transform duration-300 active:rotate-180" />
            </button>
          </div>

          {/* Answer Input Field */}
          <div className="relative flex-1">
            <input
              ref={inputRef}
              id="captcha-answer"
              name="captchaAnswer"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={value || ""}
              onChange={handleInputChange}
              placeholder="Answer"
              autoComplete="off"
              aria-label="Security verification answer"
              aria-invalid={!!(error && isTouched)}
              aria-describedby={error && isTouched ? "captcha-error-msg" : undefined}
              className={`h-11 w-full rounded-lg border bg-white px-3.5 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none transition-all dark:bg-[#374151] dark:text-[#F9FAFB] ${
                isValid
                  ? "border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-emerald-500"
                  : error && isTouched
                  ? "border-danger focus:border-danger focus:ring-2 focus:ring-danger/20"
                  : "border-[#E5E7EB] hover:border-[#b97a32]/50 focus:border-[#b97a32] focus:ring-2 focus:ring-[#b97a32]/20 dark:border-[#4B5563] dark:hover:border-[#b97a32]/50 dark:focus:border-[#b97a32] dark:focus:ring-[#b97a32]/20"
              }`.trim()}
            />

            {/* Status Feedback Icon */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              {isValid ? (
                <FiCheckCircle className="h-4 w-4 text-emerald-500" />
              ) : error && isTouched ? (
                <FiAlertCircle className="h-4 w-4 text-danger" />
              ) : null}
            </div>
          </div>
        </div>

        {/* Inline Feedback Message */}
        {error && isTouched ? (
          <p
            id="captcha-error-msg"
            className="mt-2 flex items-center gap-1 text-xs font-medium text-danger"
          >
            <FiAlertCircle className="h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        ) : isValid ? (
          <p className="mt-2 flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <FiCheckCircle className="h-3.5 w-3.5 shrink-0" />
            <span>Security verification complete</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default MathCaptcha;
